import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Check, X, Loader2 } from 'lucide-react';
import { colors, borderRadius } from '../../styles/tokens';

/**
 * EditableField — Campo editável inline
 * 
 * Tipos suportados:
 * - text: input de texto livre
 * - number: input numérico
 * - currency: input numérico com máscara R$
 * - time: input de horário (HH:mm)
 * - date: input de data (YYYY-MM-DD)
 * - select: dropdown com opções
 * 
 * Comportamento:
 * - Click no valor → abre edição
 * - Enter ou blur → salva (se mudou)
 * - Esc → cancela
 * - Hover mostra ícone de lápis sutil
 * - Flash verde ao salvar com sucesso
 * 
 * Props:
 * @param {*} value - Valor atual
 * @param {function} onSave - Callback async ao confirmar edição
 * @param {string} type - Tipo do campo
 * @param {Array} options - Para type='select': [{value, label}] ou ['string']
 * @param {string} placeholder - Texto quando vazio
 * @param {boolean} saving - Estado de salvamento externo
 * @param {boolean} saved - Flash de sucesso externo
 * @param {function} formatDisplay - Formata valor para exibição (opcional)
 * @param {function} validate - Validação (retorna string de erro ou null)
 * @param {object} style - Estilo adicional no container
 * @param {boolean} disabled - Desabilita edição
 * @param {string} suffix - Texto após o valor (ex: 'kg')
 */
export default function EditableField({
  value,
  onSave,
  type = 'text',
  options = [],
  placeholder = '-',
  saving = false,
  saved = false,
  formatDisplay,
  validate,
  style = {},
  disabled = false,
  suffix = '',
  inputStyle = {},
}) {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [validationError, setValidationError] = useState(null);
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef(null);

  // Sincroniza valor externo
  useEffect(() => {
    if (!editing) {
      setTempValue(value);
    }
  }, [value, editing]);

  // Foca input ao abrir edição
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' || type === 'number' || type === 'currency') {
        inputRef.current.select();
      }
    }
  }, [editing, type]);

  const startEditing = () => {
    if (disabled || saving) return;
    setTempValue(value ?? '');
    setValidationError(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setTempValue(value);
    setValidationError(null);
    setEditing(false);
  };

  const handleSave = async () => {
    // Normaliza valor
    let finalValue = tempValue;
    if (type === 'number' || type === 'currency') {
      finalValue = parseFloat(tempValue) || 0;
    }

    // Validação
    if (validate) {
      const err = validate(finalValue);
      if (err) {
        setValidationError(err);
        return;
      }
    }

    // Não salva se não mudou
    if (finalValue === value) {
      setEditing(false);
      return;
    }

    setEditing(false);
    if (onSave) {
      await onSave(finalValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  // Formata valor para exibição
  const displayValue = () => {
    if (value === null || value === undefined || value === '') return placeholder;
    if (formatDisplay) return formatDisplay(value);
    if (type === 'currency') {
      return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    }
    if (type === 'date' && value) {
      const d = new Date(value + 'T00:00:00');
      return d.toLocaleDateString('pt-BR');
    }
    if (type === 'select' && options.length > 0) {
      const opt = options.find(o => (typeof o === 'object' ? o.value : o) === value);
      return typeof opt === 'object' ? opt.label : (opt || value);
    }
    return `${value}${suffix ? ` ${suffix}` : ''}`;
  };

  // ── Modo edição ──
  if (editing) {
    const baseInputStyle = {
      padding: '0.35rem 0.5rem',
      border: `1.5px solid ${colors.primary}`,
      borderRadius: '4px',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      fontFamily: 'inherit',
      color: colors.text.primary,
      backgroundColor: '#f0f4ff',
      outline: 'none',
      minWidth: '60px',
      boxSizing: 'border-box',
      ...inputStyle,
    };

    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', ...style }}>
        {type === 'select' ? (
          <select
            ref={inputRef}
            value={tempValue}
            onChange={(e) => { setTempValue(e.target.value); setValidationError(null); }}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            style={{ ...baseInputStyle, cursor: 'pointer' }}
          >
            {options.map((opt) => {
              const val = typeof opt === 'object' ? opt.value : opt;
              const label = typeof opt === 'object' ? opt.label : opt;
              return <option key={val} value={val}>{label}</option>;
            })}
          </select>
        ) : type === 'currency' ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
            <span style={{ fontSize: 'inherit', color: colors.text.muted }}>R$</span>
            <input
              ref={inputRef}
              type="number"
              step="0.01"
              min="0"
              value={tempValue}
              onChange={(e) => { setTempValue(e.target.value); setValidationError(null); }}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              style={{ ...baseInputStyle, width: '110px' }}
            />
          </span>
        ) : (
          <input
            ref={inputRef}
            type={type === 'currency' ? 'number' : type}
            value={tempValue}
            onChange={(e) => { setTempValue(e.target.value); setValidationError(null); }}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            style={{
              ...baseInputStyle,
              width: type === 'time' ? '80px'
                : type === 'date' ? '140px'
                : type === 'number' ? '80px'
                : '100%',
            }}
          />
        )}
        {validationError && (
          <span style={{ fontSize: '0.7rem', color: '#ef4444', whiteSpace: 'nowrap' }}>
            {validationError}
          </span>
        )}
      </span>
    );
  }

  // ── Modo exibição ──
  const containerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    cursor: disabled ? 'default' : 'pointer',
    borderRadius: '3px',
    padding: '1px 4px',
    margin: '-1px -4px',
    transition: 'background-color 0.15s ease',
    backgroundColor: saved ? 'rgba(80, 207, 173, 0.15)' : (hovered && !disabled ? 'rgba(9, 7, 119, 0.04)' : 'transparent'),
    borderBottom: disabled ? 'none' : `1px dashed ${hovered ? colors.primary : 'transparent'}`,
    ...style,
  };

  return (
    <span
      style={containerStyle}
      onClick={startEditing}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={disabled ? undefined : 'Clique para editar'}
    >
      <span style={{
        transition: 'color 0.15s',
        color: saved ? '#059669' : undefined,
      }}>
        {displayValue()}
      </span>

      {saving && (
        <Loader2 size={12} color={colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
      )}

      {saved && !saving && (
        <Check size={12} color="#059669" strokeWidth={2.5} />
      )}

      {hovered && !disabled && !saving && !saved && (
        <Pencil size={11} color={colors.text.muted} style={{ opacity: 0.5, flexShrink: 0 }} />
      )}
    </span>
  );
}
